import React, { KeyboardEvent, useState } from 'react';
import { X, Plus, Filter, Split } from 'lucide-react';
import { FeatureFlagConditions } from '@/client/types/feature-flags';

// ─── Tag input for string[] / number[] fields ─────────────────────────────────

interface TagInputProps {
  label: string;
  hint?: string;
  placeholder?: string;
  values: (string | number)[];
  numeric?: boolean;
  onChange: (next: (string | number)[]) => void;
}

const TagInput: React.FC<TagInputProps> = ({
  label,
  hint,
  placeholder,
  values,
  numeric = false,
  onChange,
}) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const raw = draft.trim().replace(/,$/, '').trim();
    if (!raw) return;
    if (numeric) {
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      if (!values.includes(n)) onChange([...values, n]);
    } else if (!values.includes(raw)) {
      onChange([...values, raw]);
    }
    setDraft('');
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  const remove = (v: string | number) => onChange(values.filter((x) => x !== v));

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 bg-white p-2 focus-within:border-gray-400">
        {values.map((v) => (
          <span
            key={String(v)}
            className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="text-gray-400 hover:text-gray-700"
              aria-label={`Remove ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <div className="flex flex-1 items-center gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            onBlur={commit}
            inputMode={numeric ? 'numeric' : 'text'}
            placeholder={values.length ? '' : placeholder}
            className="min-w-[80px] flex-1 border-none bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-gray-400"
          />
          {draft && (
            <button
              type="button"
              onClick={commit}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Add"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Conditions builder ────────────────────────────────────────────────────────

interface ConditionsBuilderProps {
  value: FeatureFlagConditions;
  onChange: (next: FeatureFlagConditions) => void;
}

const ConditionsBuilder: React.FC<ConditionsBuilderProps> = ({ value, onChange }) => {
  const patch = (p: Partial<FeatureFlagConditions>) => onChange({ ...value, ...p });

  const asStrings = (v?: (string | number)[]) => (v ?? []).map(String);
  const asNumbers = (v?: (string | number)[]) =>
    (v ?? []).map((x) => Number(x)).filter((n) => Number.isFinite(n));

  const percentageEnabled = value.percentage != null;

  return (
    <div className="space-y-5">
      {/* Eligibility filters — AND */}
      <section className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-800">Eligibility filters</h4>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
            all must pass · AND
          </span>
        </div>
        <div className="space-y-4">
          <TagInput
            label="Regions"
            hint="e.g. GH, NG"
            placeholder="Add a region and press Enter"
            values={value.region ?? []}
            onChange={(next) => patch({ region: asStrings(next) })}
          />
          <TagInput
            label="Roles"
            hint="e.g. staff, admin"
            placeholder="Add a role and press Enter"
            values={value.roles ?? []}
            onChange={(next) => patch({ roles: asStrings(next) })}
          />
          <TagInput
            label="IP addresses"
            hint="exact IPs / CIDR"
            placeholder="Add an IP and press Enter"
            values={value.ip ?? []}
            onChange={(next) => patch({ ip: asStrings(next) })}
          />
        </div>
      </section>

      {/* Rollout gate — OR */}
      <section className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Split className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-800">Rollout gate</h4>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
            any may match · OR
          </span>
        </div>
        <div className="space-y-4">
          <TagInput
            label="User IDs"
            hint="numeric uids"
            placeholder="Add a uid and press Enter"
            numeric
            values={value.uids ?? []}
            onChange={(next) => patch({ uids: asNumbers(next) })}
          />
          <TagInput
            label="Organization IDs"
            hint="numeric org ids"
            placeholder="Add an org id and press Enter"
            numeric
            values={value.orgs ?? []}
            onChange={(next) => patch({ orgs: asNumbers(next) })}
          />

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={percentageEnabled}
                onChange={(e) =>
                  patch({ percentage: e.target.checked ? (value.percentage ?? 10) : undefined })
                }
                className="h-4 w-4 rounded border-gray-300"
              />
              Percentage rollout
              <span className="text-xs font-normal text-gray-400">
                stable per-flag hashed slice
              </span>
            </label>
            {percentageEnabled && (
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={value.percentage ?? 0}
                  onChange={(e) => patch({ percentage: Number(e.target.value) })}
                  className="h-2 flex-1 cursor-pointer"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={value.percentage ?? 0}
                    onChange={(e) => {
                      const n = Math.min(100, Math.max(0, Number(e.target.value)));
                      patch({ percentage: Number.isFinite(n) ? n : 0 });
                    }}
                    className="w-16 rounded-md border border-gray-200 px-2 py-1 text-sm"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <p className="text-xs text-gray-500">
        Eligibility filters and the rollout gate are combined with <strong>AND</strong>. A user
        must pass every eligibility filter <em>and</em> match at least one rollout gate. Example:
        roles <code>staff</code> + <code>10%</code> = &ldquo;staff, and a stable 10% of them.&rdquo;
      </p>
    </div>
  );
};

export default ConditionsBuilder;
